// Vue core libraries - Pinia, VueUse & Vue
import { acceptHMRUpdate, defineStore, storeToRefs } from "pinia";
import { computed, ref, watch } from "vue";

import { cache, CacheClosedError, courses } from "@/services/cache";

// TMI - content type and query definitions

import type { Course, Lesson, Section } from "@tmi/types/entities";

// Non-reactive variables
import { createDefaultLogger } from "@/types/console-logger";
const logger = createDefaultLogger();

import { useAuthStore } from "@/stores/Auth";
import { courseSeedData } from "@/services/seed-service";
import { useProductsStore } from "@/stores/Products";

// State values
const initialized= computed< boolean >( () => allCourses.value.length > 0 );
const isLoading = computed< boolean >( () => loading.value );
const loading = ref< boolean >( false );
const refreshing = ref< boolean >( false );

const selectedCourse = computed< Course | undefined >( () => {
    const contextBasedSelectedCourse = allCourses.value.find( ( course: Course ): boolean =>
        selectedCourseSlug.value === course.slug
    );

    if ( contextBasedSelectedCourse ) {
        return contextBasedSelectedCourse;
    }
} );

const selectedLesson = ref< Lesson | undefined >();

const defaultCourses: Course[] = [];
const allCourses = ref< Course[] >( defaultCourses );

const selectedCourseSlug = ref< string | undefined >();

// WHY: The previous setSelectedCourseByProductSlug ran exactly once at component
// setup. If `allCourses` had not finished loading by then (which is the normal
// case — courses load via appStore.initializeAsync inside the wrapper's onMounted,
// AFTER setup), the lookup found nothing and never re-ran. CourseOverview's
// `v-if="selectedCourse"` therefore stayed false forever and the page rendered
// blank below the tab bar. We now remember the requested productSlug and resolve
// it reactively whenever `allCourses` becomes populated.
const pendingProductSlug = ref< string | undefined >();

export const useCoursesStore = defineStore( 'courses', () => {
    const { isAuthenticated } = storeToRefs( useAuthStore() );

    function setSelectCourseByCourseSlug( courseSlug: string ) {
        selectedCourseSlug.value = courseSlug;
    }

    function tryResolvePendingProductSlug() {
        if ( !pendingProductSlug.value || allCourses.value.length === 0 ) return;

        const product = useProductsStore().productBySlug( pendingProductSlug.value );
        if ( !product ) return;

        const contextBasedSelectedCourse = allCourses.value.find( ( course: Course ): boolean =>
            product.id === course.assoc_product_ids[ 0 ]
        );

        if ( contextBasedSelectedCourse ) {
            selectedCourseSlug.value = contextBasedSelectedCourse.slug;
            pendingProductSlug.value = undefined;
        }
    }

    function setSelectedCourseByProductSlug( productSlug: string ) {
        pendingProductSlug.value = productSlug;
        // Try immediately — works fast-path when courses are already loaded.
        tryResolvePendingProductSlug();
    }

    // Reactively retry the lookup once courses populate.
    watch( allCourses, () => tryResolvePendingProductSlug(), { deep: false } );

    function reset(): void {
        allCourses.value.splice( 0 );
        selectedCourseSlug.value = undefined;
        selectedLesson.value = undefined;
        loading.value = false;
        refreshing.value = false;
    }

    async function initializeAsync(): Promise< void > {
        // If not authenticated or the application context has not yet been created, return immediately
        if ( !isAuthenticated.value ) {
            logger.info( `[ Courses ].initialize() exiting - not authenticated => ${ !isAuthenticated.value } ]` );
            return;
        }

        if ( initialized.value || isLoading.value ) {
            logger.debug( '[ Courses ].initialize() exiting - already processed...' );
            return;
        }

        try {
            loading.value = true;

            const t0: number = performance.now();
            const uniqueCourses = new Set< Course >();
            // Wait for the database to open before the first read.
            await cache.ready();

            if ( !await courses.isEmpty() ) {
                // Clear the contents of the backing store
                allCourses.value.splice( 0 );

                for ( const course of await courses.all() ) {
                    uniqueCourses.add( course );
                }
                allCourses.value = [ ...uniqueCourses ];

                const t1: number = performance.now();
                logger.info( `(local) Loaded ${ allCourses.value.length } courses in ${ t1 - t0 } ms` );
            } else {
                allCourses.value = courseSeedData();

                const t1: number = performance.now();
                logger.info( `Fetching & mapping ${ allCourses.value.length } course items took ${ t1 - t0 } ms` );
            }
        } catch ( err: any ) {
            if ( err instanceof CacheClosedError ) { return; }   // a newer build upgraded the cache; this tab is reloading
            logger.error( '[ Courses ].initializeAsync() error:', err );
        } finally {
            // Clear the "loading" flag
            loading.value = false;
        }
    }

    function selectLessonBySlug( slug: string ): void {
        const allLessons: Lesson[] = [];
        selectedCourse.value?.sections.map( ( section: Section ) => allLessons.push( ...section.lessons ) );
        selectedLesson.value = allLessons.find( ( lesson: Lesson ) => slug === lesson.slug );
    }

    return {
        // ACTIONS
        initializeAsync,
        reset,
        selectLessonBySlug,
        setSelectCourseByCourseSlug,
        setSelectedCourseByProductSlug,

        // GETTERS
        allCourses,
        initialized,
        isLoading,
        selectedCourse,
        selectedLesson
    }
} );

if ( import.meta.hot ) {
    import.meta.hot.accept( acceptHMRUpdate( useCoursesStore, import.meta.hot ) );
}
